import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import SearchBar from "../components/SearchBar";
import { carsApi as adminCarsApi, locationsApi as adminLocationsApi, publicCarsApi, publicLocationsApi } from "../../api";
const carsApi = publicCarsApi;
const locationsApi = publicLocationsApi;
// eslint-disable-next-line no-unused-vars
const _admin = { adminCarsApi, adminLocationsApi };
import { Infinity as InfinityIcon, ShieldCheck, Phone, BadgeDollarSign, MapPin, Users, ArrowRight, Star } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1610809589386-9ea41901eb54?auto=format&fit=crop&w=1920&q=80";

export default function Home() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    carsApi.listAll().then((d) => setCars(d.slice(0, 6))).catch(() => setCars([]));
    locationsApi.list().then(setLocations).catch(() => setLocations([]));
  }, []);

  const featured = cars;

  return (
    <div>
      {/* Hero */}
      <section className="relative">
        <div
          className="relative bg-cover bg-center min-h-[560px] md:min-h-[640px]"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-black/75" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 md:pt-24 pb-32 md:pb-40 text-white">
            <p className="dams-brand-sub text-red-400 mb-3">{t("hero.tagline")}</p>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight max-w-3xl leading-[1.05]">
              {t("hero.title")}
            </h1>
            <p className="mt-5 text-base md:text-lg text-gray-200 max-w-2xl">{t("hero.subtitle")}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={() => navigate("/cars")} className="pill-btn primary inline-flex items-center gap-2" style={{ padding: "0.85rem 1.4rem" }}>
                {t("hero.ctaSearch")} <ArrowRight size={16} />
              </button>
              <button onClick={() => navigate("/cars")} className="pill-btn outline" style={{ padding: "0.85rem 1.4rem" }}>
                {t("hero.ctaBrowse")}
              </button>
            </div>
          </div>
        </div>

        {/* Search bar overlapping hero */}
        <div className="relative -mt-20 md:-mt-24 max-w-6xl mx-auto px-4 sm:px-6 z-10">
          <SearchBar />
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-20">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">{t("features.h")}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: <InfinityIcon size={22} />, key: "a" },
            { icon: <ShieldCheck size={22} />, key: "b" },
            { icon: <Phone size={22} />, key: "c" },
            { icon: <BadgeDollarSign size={22} />, key: "d" },
          ].map((f) => (
            <div key={f.key} className="card-soft p-6 hover:shadow-lg transition-shadow">
              <div className="w-11 h-11 rounded-xl bg-red-50 text-red-600 flex items-center justify-center mb-4">
                {f.icon}
              </div>
              <h3 className="font-bold mb-1">{t(`features.${f.key}`)}</h3>
              <p className="text-sm text-gray-600">{t(`features.${f.key}Desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured cars */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-20">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">{t("cars.title")}</h2>
          <button onClick={() => navigate("/cars")} className="text-sm font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1">
            {t("hero.ctaBrowse")} <ArrowRight size={14} />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map((c) => (
            <div key={c.id} className="card-soft overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group" onClick={() => navigate(`/cars/${c.id}`)}>
              <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                {c.image ? (
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : <div className="w-full h-full" />}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-lg leading-tight">{c.name}</h3>
                    <p className="text-xs uppercase tracking-wider text-gray-500 mt-1">{c.category} · {c.seats} {t("cars.seatsLabel")}</p>
                  </div>
                  <div className="flex items-center gap-1 text-amber-500">
                    <Star size={14} fill="currentColor" /><span className="text-sm font-semibold text-gray-800">4.9</span>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-2 flex items-center gap-1"><MapPin size={12} className="text-red-500" /> {c.location}</div>
                <div className="mt-4 flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-extrabold text-gray-900">${c.price}</span>
                    <span className="text-sm text-gray-500">{t("cars.perDay")}</span>
                  </div>
                  <button className="pill-btn dark" style={{ padding: "0.5rem 1rem" }} onClick={(e) => { e.stopPropagation(); navigate(`/cars/${c.id}`); }}>
                    {t("cars.bookNow")}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Locations */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-20">
        <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-2">{t("locations.title")}</h2>
        <p className="text-gray-600 mb-8">{t("locations.subtitle")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((l) => (
            <div key={l.id} className="card-soft p-5 flex items-start gap-4 hover:shadow-lg transition-shadow">
              <div className="location-pin"><MapPin size={20} /></div>
              <div className="flex-1">
                <h4 className="font-bold">{l.name}</h4>
                <p className="text-sm text-gray-600">{l.address}</p>
                <div className="mt-2 text-xs flex items-center gap-3">
                  <span className="text-red-strong">{t("locations.taxLabel")} {l.tax}%</span>
                  <span className="text-gray-700 font-semibold">{t("locations.minLabel")} {l.minBookingDays} {l.minBookingDays === 1 ? t("locations.day") : t("locations.days")}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-20">
        <div className="relative overflow-hidden rounded-3xl bg-[#0d0d0d] text-white p-8 md:p-14">
          <div className="relative z-10 max-w-2xl">
            <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight">{t("hero.title")}</h3>
            <p className="mt-3 text-gray-300">{t("hero.subtitle")}</p>
            <button onClick={() => navigate("/cars")} className="pill-btn primary mt-5 inline-flex items-center gap-2" style={{ padding: "0.85rem 1.4rem" }}>
              {t("hero.ctaSearch")} <ArrowRight size={16} />
            </button>
          </div>
          <div className="absolute -right-10 -bottom-10 opacity-20">
            <Users size={300} />
          </div>
        </div>
      </section>
    </div>
  );
}
